import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectors as fdeInboxSelectors } from 'src/features/fdeInbox/fdeInboxSlice';
import {
  getRentalHistoryPropertyEnabled,
  selectors as propertySelectors
} from 'src/features/property';
import { useAppDispatch } from 'src/store';

/**
 * Extracts property IDs from FDE inbox filter string
 * Format: "property_id@>uuid1|uuid2|uuid3,other_filter=value"
 * @param filterString - The filter string from FDE inbox
 * @returns Array of property IDs or undefined if no property filter
 */
const extractPropertyIds = (filterString?: string): string[] | undefined => {
  if (!filterString) {
    return undefined;
  }

  const propertyFilter = filterString.split(',').find((filter) => filter.includes('property_id@>'));

  if (!propertyFilter) {
    return undefined;
  }

  const [, propertyIds] = propertyFilter.split('@>');
  return propertyIds ? propertyIds.split('|').filter(Boolean) : undefined;
};

/**
 * Custom hook to check if rental history is enabled
 * Uses backend API to efficiently check property features
 * Respects property filtering from FDE Inbox - refetches when property selection changes
 * @returns boolean indicating if rental history is enabled for the selected/accessible properties
 */
export const useRentalHistoryEnabled = (): boolean => {
  const dispatch = useAppDispatch();

  const hasRentalHistoryPropertyEnabled = useSelector(
    propertySelectors.hasRentalHistoryPropertyEnabled.data
  );
  const isLoading = useSelector(propertySelectors.hasRentalHistoryPropertyEnabled.isLoading);
  const isIdle = useSelector(propertySelectors.hasRentalHistoryPropertyEnabled.isIdle);

  // Get FDE inbox filters to track property selection changes
  const { filters } = useSelector(fdeInboxSelectors);

  // Extract property IDs from filter string
  const propertyIds = useMemo(() => {
    return extractPropertyIds(filters?.filter);
  }, [filters?.filter]);

  // Fetch rental history status when property filter changes
  useEffect(() => {
    dispatch(getRentalHistoryPropertyEnabled(propertyIds));
  }, [dispatch, propertyIds]);

  // Return false if still loading to avoid flashing content
  if (isLoading || isIdle) {
    return false;
  }

  // Return true only if at least one property has the feature
  return hasRentalHistoryPropertyEnabled === true;
};

export default useRentalHistoryEnabled;
